import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createRpiDrawerView, RpiDrawer, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { RpiDrawerField } from "../../../rpi-core/drawer/RpiDrawerField";
import { formatCurrency } from "../../../utils/misc.util";
import { shouldRetry } from "../../../utils/query.util";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";
import { createStyles, Stack } from "@mantine/core";
import { Button, tokens } from "@blurb/codex-react";

export const OpenApiProductView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { id: stringId } = useParams();
    const id = React.useMemo(() => stringId && Number.parseInt(stringId), []);

    React.useEffect(() => {
      if (!stringId) onClose();
    }, [stringId]);

    const { data, isLoading, error } = GraphQL.useOpenApiProductQuery(
      { id: id || -1 },
      {
        enabled: !!stringId && opened,
        refetchOnMount: true,
        retry: shouldRetry(onClose),
      }
    );
    const openApiProduct = React.useMemo(() => data?.openapiProduct, [data]);

    return (
      <RpiDrawer title="Open API Product details" isLoading={isLoading} error={error} opened={opened} onClose={onClose}>
        {openApiProduct && <OpenApiProductInfo openApiProduct={openApiProduct} />}
      </RpiDrawer>
    );
  }
);

interface OpenApiProductInfoProps {
  openApiProduct: GraphQLQueryData<GraphQL.OpenApiProductQuery>;
}
const OpenApiProductInfo: React.FC<OpenApiProductInfoProps> = ({ openApiProduct }) => {
  const navigate = useNavigate();
  const { basePrice, pageCost } = React.useMemo(() => {
    if (!openApiProduct.productPricing || openApiProduct.productPricing.length === 0) {
      return {};
    }

    // TODO: Selectable currency?
    const pricing = openApiProduct.productPricing[0];
    return {
      basePrice: formatCurrency(pricing.currency, pricing.basePrice),
      pageCost: formatCurrency(pricing.currency, pricing.perPageCost),
    };
  }, [openApiProduct.productPricing]);

  return (
    <>
      <RpiDrawerField label="Name" children={openApiProduct.name} />
      <RpiDrawerField label="Description" children={openApiProduct.description} />
      <RpiDrawerField label="Base Price" children={basePrice} />
      <RpiDrawerField label="Page Cost" children={pageCost} />
      <RpiDrawerField label="Open API Sku" children={openApiProduct.openapiSku} />
      <RpiDrawerField label="UOS Sku" children={openApiProduct.uosSku} />
      <RpiDrawerField label="UOS Orientation" children={openApiProduct.uosOrientation} />
      <RpiDrawerField label="UOS Media Option" children={openApiProduct.uosMediaOption} />
      <Button
        variant="outlined"
        intent="brand"
        data-testid="editSkuProductDetailsButton"
        onClick={() => navigate(`../edit/${openApiProduct.id}`)}
      >
        Edit SKU
      </Button>
    </>
  );
};

const useSkuDrawerStyles = createStyles({
  root: {
    padding: `0 ${tokens.spacing[6]}`,
  },
});

export type OpenApiProductCreateViewProps = {
  isOpened: boolean;
  onClose: () => void;
};
export function OpenApiProductCreateView({
  isOpened,
  onClose,
  children,
}: React.PropsWithChildren<OpenApiProductCreateViewProps>) {
  const { classes } = useSkuDrawerStyles();
  return (
    <RpaDrawer title="Add New SKU" opened={isOpened} onClose={onClose} data-testid="addNewSkuDrawer">
      <Stack className={classes.root}>{children}</Stack>
    </RpaDrawer>
  );
}

export type OpenApiProductEditViewProps = {
  isOpened: boolean;
  onClose: () => void;
};
export function OpenApiProductEditView({
  isOpened,
  onClose,
  children,
}: React.PropsWithChildren<OpenApiProductEditViewProps>) {
  const { classes } = useSkuDrawerStyles();
  return (
    <RpaDrawer title="Edit SKU" opened={isOpened} onClose={onClose} data-testid="editSkuDrawer">
      <Stack className={classes.root}>{children}</Stack>
    </RpaDrawer>
  );
}
